import { Dialog, TooltipV2 } from '@cimplico/components';
import { useState } from 'react';
import { toastNotification } from '../../../components/toast/Toast';
import { useUpdateUsers } from '../../../queries/use-update-users';
import { User } from '../../../types/user';
import UserAvatar from '../../../components/avatar/UserAvatar';

type AvatarColourPickerDialogProps = {
    user: User;
    defaultColour: string;
    onClose: () => void;
};

function AvatarColourPickerDialog({ user, defaultColour, onClose }: AvatarColourPickerDialogProps): JSX.Element {
    const [selectedColour, setSelectedColour] = useState(defaultColour);

    const colours: { [colour: string]: string } = {
        blue: 'bg-blue-500 dark:bg-blue-400',
        green: 'bg-green-500 dark:bg-green-400',
        yellow: 'bg-yellow-500 dark:bg-yellow-400',
        orange: 'bg-orange-500 dark:bg-orange-400',
        red: 'bg-red-500 dark:bg-red-400',
        indigo: 'bg-indigo-500 dark:bg-indigo-400',
        violet: 'bg-violet-500 dark:bg-violet-400',
        pink: 'bg-pink-500 dark:bg-pink-400',
        gray: 'bg-gray-500 dark:bg-gray-400',
    };

    const updateUserQuery = useUpdateUsers();

    function handleUpdate(): void {
        if (selectedColour === defaultColour) {
            onClose();
        }

        updateUserQuery.mutate([{ id: user.id, fields: { profileHexColour: selectedColour } }], {
            onSuccess: () => {
                onClose();
                toastNotification('success', "Avatar's colour updated!");
            },
            onError: (err) => {
                toastNotification(
                    'error',
                    "Unable to update avatar's colour!",
                    err.response?.data.results[0].error.fields.profileHexColour
                );
            },
        });
    }

    return (
        <Dialog
            type="info"
            title="Change avatar colour"
            description="Select a background colour for your profile picture."
            isOpen
            primaryAction="Save"
            secondaryAction="Cancel"
            onClose={onClose}
            onSecondaryActionClick={onClose}
            onPrimaryActionClick={handleUpdate}
            isLoading={updateUserQuery.isLoading}
        >
            <div className="flex gap-x-4 pb-4 items-center pr-12">
                <UserAvatar size="jumbo" ring={true} user={user} colour={selectedColour} />
                <div className="flex gap-1">
                    {Object.keys(colours).map((colour, idx) => {
                        const isSelected = selectedColour === colour;
                        return (
                            <TooltipV2 key={idx} message={colour} position="bottom">
                                <button
                                    className={`flex items-center justify-center w-[22px] h-[22px] rounded-md ${
                                        isSelected && 'ring-1 ring-offset-1 ring-indigo-600'
                                    } ${
                                        colours[colour]
                                    } hover:ring-1 hover:ring-offset-1 hover:ring-indigo-600 focus:outline-none focus:ring-1 focus:ring-offset-1 focus:ring-indigo-600`}
                                    onClick={(e): void => {
                                        if (isSelected) {
                                            return;
                                        }
                                        setSelectedColour(colour);
                                        e.currentTarget.blur();
                                    }}
                                />
                            </TooltipV2>
                        );
                    })}
                </div>
            </div>
        </Dialog>
    );
}

export default AvatarColourPickerDialog;
